import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import PenOutlined from '@/images/PenOutlined.svg';
import theme from '@/theme';
import styled from '@emotion/styled';
import { Tooltip, tooltipClasses, TooltipProps } from '@mui/material';
import { ChangeEvent, ForwardedRef, forwardRef, useEffect, useRef, useState } from 'react';

interface Props {
    testId?: string;
    value: string;
    onChange: (value: string) => void;
    autofocus?: boolean;
    color?: string;
    onBlur?: (value: string) => void;
    disabled?: boolean;
    placeholder?: string;
    fontSize?: number;
    showIcon?: boolean;
    className?: string;
    fontWeight?: number;
    capitalize?: boolean;
    showTooltip?: boolean;
    staticWidth?: boolean;
}

const ToolTip = styled(({ className, ...props }: TooltipProps) => <Tooltip {...props} classes={{ popper: className }} />)({
    [`& .${tooltipClasses.tooltip}`]: {
        maxWidth: '40rem',
        fontSize: '1.6rem',
        backgroundColor: theme.colors.secondary[900],
    },
});

const StyledInput = styled.input<{ width: string; fontSize?: number; fontWeight?: number; capitalize?: boolean; showTooltip?: boolean }>`
    background: transparent;
    border: none;
    outline: none;
    font-size: ${({ fontSize }) => (fontSize ? `${fontSize}px` : '16px')};
    font-weight: ${({ fontWeight }) => fontWeight ?? '400'};
    line-height: ${({ fontSize }) => (fontSize ? `${fontSize + 4}px` : '20px')};
    text-transform: ${({ capitalize }) => (capitalize ? 'capitalize' : 'none')};
    width: ${props => props.width};
    padding: 0;
    outline-offset: 0;

    :hover,
    :focus {
        text-decoration-line: ${({ disabled }) => (disabled ? 'none' : 'underline')};
    }
    ::selection {
        background: ${({ theme }) => theme.colors.primary.bg};
    }
`;

const TextLengthIndicator = styled.div<{ fontSize?: number; fontWeight?: number; capitalize?: boolean }>`
    font-size: ${({ fontSize }) => (fontSize ? `${fontSize}px` : '16px')};
    font-weight: ${({ fontWeight }) => (fontWeight ? fontWeight + 100 : 500)};
    text-transform: ${({ capitalize }) => (capitalize ? 'capitalize' : 'none')};
    width: auto;
    display: inline-block;
    visibility: hidden;
    position: fixed;
    overflow: auto;
`;

const StyledInputWrapper = styled.div<{ width: string; color?: string; fontSize?: number; withIcon: boolean }>`
    font-size: ${({ fontSize }) => (fontSize ? `${fontSize}px` : '16px')};
    position: relative;
    color: ${props => props.color || 'inherit'};
    display: flex;
    width: ${props => props.width};
    ${props => (props.withIcon ? 'padding-right: 24px' : '')};
    align-items: center;
`;

const StyledIcon = styled(MaskedIcon)`
    right: 0;
    top: 3px;
    position: absolute;
    cursor: pointer;
`;

const MAX_LENGTH = 88;
const DEFAULT_INPUT_WIDTH_BUFFER = 3;

export const EditableTitle = forwardRef(
    (
        {
            onChange,
            testId = 'editable-title',
            value,
            autofocus,
            color,
            fontSize,
            placeholder,
            onBlur,
            disabled,
            showIcon,
            className,
            fontWeight,
            capitalize,
            showTooltip,
            staticWidth,
        }: Props,
        ref: ForwardedRef<HTMLDivElement>
    ) => {
        const [width, setWidth] = useState<number>(0);
        const inputRef = useRef<HTMLInputElement>(null);
        const textLengthIndicatorRef = useRef<HTMLDivElement>(null);

        useEffect(() => {
            if (autofocus && inputRef.current) {
                handleFocus();
            }
        }, [autofocus, inputRef.current]);

        const setIndicatorWidth = (newValue: string) => {
            const noValue = !newValue || newValue === '';

            if (textLengthIndicatorRef.current && !noValue) {
                textLengthIndicatorRef.current.innerText = newValue;
                setWidth(textLengthIndicatorRef.current.clientWidth + DEFAULT_INPUT_WIDTH_BUFFER);
            }

            if (textLengthIndicatorRef.current && noValue && placeholder) {
                textLengthIndicatorRef.current.innerText = placeholder;
                setWidth(textLengthIndicatorRef.current.clientWidth + DEFAULT_INPUT_WIDTH_BUFFER);
            }
        };

        useEffect(() => {
            setIndicatorWidth(value);
        }, [textLengthIndicatorRef.current, value]);

        const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value;

            if (newValue.length <= MAX_LENGTH) {
                if (!staticWidth) {
                    setIndicatorWidth(newValue);
                }
                onChange(newValue);
            }
        };

        const handleSelect = () => {
            inputRef.current?.select();
        };

        const handleFocus = () => {
            inputRef.current?.focus();
        };

        const handleBlur = () => {
            onBlur?.(value);
        };

        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
                inputRef.current?.blur();
            }
        };

        const styledInput = (
            <StyledInput
                data-testid={testId}
                ref={inputRef}
                disabled={disabled}
                value={value}
                placeholder={placeholder}
                onChange={handleChange}
                onFocus={handleSelect}
                onClick={handleSelect}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                width={`${width}px`}
                fontSize={fontSize}
                fontWeight={fontWeight}
                capitalize={capitalize}
            />
        );
        return (
            <StyledInputWrapper
                ref={ref}
                onClick={handleFocus}
                color={color}
                width={`${width + 30}px`}
                fontSize={fontSize}
                className={className}
                withIcon={!!showIcon}>
                {showTooltip ? (
                    <ToolTip disableFocusListener disableInteractive title={value} placement="bottom-end">
                        {styledInput}
                    </ToolTip>
                ) : (
                    styledInput
                )}
                <TextLengthIndicator ref={textLengthIndicatorRef} fontSize={fontSize} fontWeight={fontWeight} capitalize={capitalize} />
                {showIcon && <StyledIcon src={PenOutlined} size="16px" onClick={handleFocus} />}
            </StyledInputWrapper>
        );
    }
);
